import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SiteCard from '@/components/base/SiteCard';
import { categories, sites } from '@/mocks/navigation';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const searchQuery = searchParams.get('q') || '';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const currentCategory = useMemo(() => {
    return categories.find((c) => c.slug === slug) || categories[0];
  }, [slug]);

  const filteredSites = useMemo(() => {
    let result = slug ? sites.filter((s) => s.category === slug) : sites;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(query) ||
          s.description.toLowerCase().includes(query) ||
          s.tags.some((t) => t.toLowerCase().includes(query))
      );
    }

    return result;
  }, [slug, searchQuery]);

  const handleCategoryClick = (catSlug: string) => {
    navigate(`/category/${catSlug}`);
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类导航', href: '/category/gongju' },
    { label: searchQuery ? `搜索: ${searchQuery}` : currentCategory.name },
  ];

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: searchQuery ? `搜索: ${searchQuery} - 91导航` : `${currentCategory.name} - 91导航`,
    description: searchQuery
      ? `91导航中与"${searchQuery}"相关的网站列表`
      : `91导航收录的${currentCategory.name}类优质网站大全`,
    url: `https://91daohang.com/category/${slug || ''}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredSites.map((site, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'WebSite',
          name: site.name,
          url: site.url,
          description: site.description,
        },
      })),
    },
  };

  return (
    <>
      <Header />
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              {searchQuery ? `搜索: ${searchQuery}` : currentCategory.name}
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              {searchQuery
                ? `找到 ${filteredSites.length} 个相关网站`
                : `浏览${currentCategory.name}类下的优质网站，共收录 ${filteredSites.length} 个网站`}
            </p>
          </div>

          <div className="flex items-center gap-1.5 pb-4 mb-6 border-b border-background-200/70 overflow-x-auto flex-nowrap">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryClick(cat.slug)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  cat.slug === slug && !searchQuery
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                <span>{cat.name}</span>
              </button>
            ))}
          </div>

          {filteredSites.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-search-line text-3xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500 text-sm">未找到相关网站，请尝试其他关键词或分类</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 pb-14" data-product-shop="">
              {filteredSites.map((site) => (
                <SiteCard
                  key={site.id}
                  name={site.name}
                  url={site.url}
                  description={site.description}
                  tags={site.tags}
                />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
      <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>
    </>
  );
}